import { useCallback, useEffect, useState } from 'react';
import { customPlansMasterApi, type FulfillCustomPlanRequest } from '@/api/customPlans.api';
import type { CustomPlanRequestResponse } from '@/api/subscriptions.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { dataTablePageProps, paginateClientList, usePagination } from '@/hooks/usePagination';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { formatDate } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function MasterCustomPlansPage() {
  const pagination = usePagination();
  const [data, setData] = useState<CustomPlanRequestResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [fulfillOpen, setFulfillOpen] = useState(false);
  const [selected, setSelected] = useState<CustomPlanRequestResponse | null>(null);
  const [form, setForm] = useState<FulfillCustomPlanRequest>({
    requestId: 0,
    planName: '',
    description: '',
    priceMonthly: 0,
    priceYearly: 0,
    maxStores: 1,
    maxEmployees: 5,
    maxProducts: 500,
    masterNotes: '',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await customPlansMasterApi.list();
      setData(rows);
      pagination.applyClientCount(rows.length);
    } catch (err) {
      showApiError(err, 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openFulfill = (row: CustomPlanRequestResponse) => {
    setSelected(row);
    setForm({
      requestId: row.id,
      planName: `Custom — ${row.organizationName ?? `Org #${row.organizationId}`}`,
      description: row.message ?? 'Custom plan for organization',
      priceMonthly: 999,
      priceYearly: 9990,
      maxStores: row.requestedStores ?? 3,
      maxEmployees: row.requestedEmployees ?? 10,
      maxProducts: row.requestedProducts ?? 1000,
      masterNotes: '',
    });
    setFulfillOpen(true);
  };

  const fulfill = async () => {
    setSaving(true);
    try {
      await customPlansMasterApi.fulfill(form);
      notify.success('Custom plan created — organization can subscribe on Subscription page');
      setFulfillOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<CustomPlanRequestResponse>[] = [
    { key: 'organizationName', header: 'Organization', render: (r) => r.organizationName ?? `#${r.organizationId}` },
    { key: 'contactEmail', header: 'Contact email', render: (r) => r.contactEmail ?? '—' },
    { key: 'status', header: 'Status' },
    {
      key: 'limits',
      header: 'Requested',
      render: (r) =>
        `${r.requestedStores ?? '—'} stores · ${r.requestedEmployees ?? '—'} staff · ${r.requestedProducts ?? '—'} products`,
    },
    { key: 'message', header: 'Message', render: (r) => r.message ?? '—' },
    { key: 'createdAt', header: 'Submitted', render: (r) => (r.createdAt ? formatDate(r.createdAt) : '—') },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'PENDING' ? (
          <Button onClick={() => openFulfill(r)}>
            Create plan
          </Button>
        ) : (
          <span className="text-xs text-slate-500">{r.status === 'FULFILLED' ? 'Plan ready' : r.status}</span>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Custom plan requests"
        description="Review organization requests and publish a private plan they can subscribe to."
        breadcrumbs={[{ label: 'Master', to: '/master' }, { label: 'Custom plans' }]}
      />
      <DataTable
        columns={columns}
        data={paginateClientList(data, pagination.page, pagination.size)}
        keyField="id"
        loading={loading}
        {...dataTablePageProps(pagination)}
      />

      <Modal
        open={fulfillOpen}
        onClose={() => setFulfillOpen(false)}
        title={selected ? `Plan for ${selected.organizationName ?? selected.organizationId}` : 'Create plan'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setFulfillOpen(false)}>
              Cancel
            </Button>
            <Button onClick={fulfill} loading={saving}>
              Publish plan
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Plan name"
            value={form.planName ?? ''}
            onChange={(e) => setForm({ ...form, planName: e.target.value })}
          />
          <Input
            label="Monthly price (INR)"
            type="number"
            value={String(form.priceMonthly)}
            onChange={(e) => setForm({ ...form, priceMonthly: Number(e.target.value) })}
          />
          <Input
            label="Yearly price (INR)"
            type="number"
            value={String(form.priceYearly ?? '')}
            onChange={(e) => setForm({ ...form, priceYearly: Number(e.target.value) })}
          />
          <Input
            label="Max stores"
            type="number"
            value={String(form.maxStores ?? '')}
            onChange={(e) => setForm({ ...form, maxStores: Number(e.target.value) })}
          />
          <Input
            label="Max employees"
            type="number"
            value={String(form.maxEmployees ?? '')}
            onChange={(e) => setForm({ ...form, maxEmployees: Number(e.target.value) })}
          />
          <Input
            label="Max products"
            type="number"
            value={String(form.maxProducts ?? '')}
            onChange={(e) => setForm({ ...form, maxProducts: Number(e.target.value) })}
          />
          <div className="sm:col-span-2">
            <Input
              label="Description"
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <Input
              label="Internal notes"
              value={form.masterNotes ?? ''}
              onChange={(e) => setForm({ ...form, masterNotes: e.target.value })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
